import React, { useState, useEffect } from 'react';
import { Routes, Route, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';
import { LogOut, Users, Menu as MenuIcon, Package, BarChart, FileText, Settings, Download, Wallet, Smartphone, Coffee, PanelLeftClose, PanelLeft, Search, Receipt, Plus, Trash2 } from 'lucide-react';
import jsPDF from 'jspdf';
import SearchableDropdown from '../components/SearchableDropdown';
import DeviceInfoModal from '../components/DeviceInfoModal';
import CafeExpenseTracker from './CafeExpenseTracker';
import LogoLoader from '../components/LogoLoader';
import RawMaterialsManagement from '../components/RawMaterialsManagement';
import RawMaterialOrders from '../components/RawMaterialOrders';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = ({ user, setUser }) => {
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState('franchises');
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [franchises, setFranchises] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [menuSearchQuery, setMenuSearchQuery] = useState('');
  const [inventory, setInventory] = useState([]);
  const [reportData, setReportData] = useState(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [newFranchise, setNewFranchise] = useState({
    franchise_name: '',
    franchise_model: 'Kiosk',
    franchise_amount_collected: '',
    owner_name: '',
    contact_number: '',
    aadhaar_no: '',
    pan_no: '',
    owner_address: '',
    franchise_address: '',
    city: '',
    pincode: '',
    district: '',
    state: '',
    taluk: '',
    date_of_opening: '',
    additional_owner_names: '',
    wifi_password: ''
  });
  const [newMenuItem, setNewMenuItem] = useState({ name: '', category: '', price: '', selected_franchises: [] });
  const [editMenuItem, setEditMenuItem] = useState(null);
  const [isEditMenuOpen, setIsEditMenuOpen] = useState(false);
  const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
  const [categoryModalFor, setCategoryModalFor] = useState('new'); // 'new' or 'edit'
  const [newCategoryName, setNewCategoryName] = useState('');
  const [categories, setCategories] = useState([]);
  const [newInventory, setNewInventory] = useState({ item_name: '', unit: '' });
  const [reportFilters, setReportFilters] = useState({ franchise_id: 'all', start_date: '', end_date: '' });
  const [isAddFranchiseOpen, setIsAddFranchiseOpen] = useState(false);
  const [isAddMenuOpen, setIsAddMenuOpen] = useState(false);
  const [isAddInventoryOpen, setIsAddInventoryOpen] = useState(false);
  const [isDeviceInfoOpen, setIsDeviceInfoOpen] = useState(false);
  const [selectedFranchise, setSelectedFranchise] = useState(null);
  const [isFranchiseDetailsOpen, setIsFranchiseDetailsOpen] = useState(false);
  const [actualsData, setActualsData] = useState(null);
  const [actualsFilters, setActualsFilters] = useState({ franchise_id: 'all', start_date: '', end_date: '' });
  const [allLedgers, setAllLedgers] = useState([]);
  const [selectedPartyLedger, setSelectedPartyLedger] = useState(null);
  const [isPartyLedgerDetailsOpen, setIsPartyLedgerDetailsOpen] = useState(false);
  
  // Expense Categories State
  const [expenseCategories, setExpenseCategories] = useState([]);
  const [newExpenseCategory, setNewExpenseCategory] = useState('');
  const [franchiseExpenseReports, setFranchiseExpenseReports] = useState([]);
  const [expenseReportFilters, setExpenseReportFilters] = useState({
    franchise_id: 'all',
    start_date: new Date(new Date().setDate(1)).toISOString().split('T')[0],
    end_date: new Date().toISOString().split('T')[0]
  });

  useEffect(() => {
    const loadData = async () => {
      setInitialLoading(true);
      await Promise.all([
        fetchFranchises(),
        fetchMenuItems(),
        fetchInventory(),
        fetchExpenseCategories()
      ]);
      setInitialLoading(false);
    };
    loadData();
  }, []);

  const fetchFranchises = async () => {
    try {
      const response = await axios.get(`${API}/franchises`);
      setFranchises(response.data.franchises || []);
    } catch (error) {
      console.error('Error fetching franchises:', error);
    }
  };

  const fetchExpenseCategories = async () => {
    try {
      const response = await axios.get(`${API}/expense-categories`);
      setExpenseCategories(response.data.categories || []);
    } catch (error) {
      console.error('Error fetching expense categories:', error);
    }
  };

  const handleAddExpenseCategory = async () => {
    if (!newExpenseCategory.trim()) {
      toast.error('Please enter a category name');
      return;
    }
    try {
      await axios.post(`${API}/expense-categories`, { name: newExpenseCategory.trim() });
      toast.success('Expense category added!');
      setNewExpenseCategory('');
      fetchExpenseCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add category');
    }
  };

  const handleDeleteExpenseCategory = async (categoryId) => {
    if (!window.confirm('Are you sure you want to delete this category?')) return;
    try {
      await axios.delete(`${API}/expense-categories/${categoryId}`);
      toast.success('Category deleted');
      fetchExpenseCategories();
    } catch (error) {
      toast.error('Failed to delete category');
    }
  };

  const fetchFranchiseExpenseReports = async () => {
    try {
      const params = {
        start_date: expenseReportFilters.start_date,
        end_date: expenseReportFilters.end_date
      };
      if (expenseReportFilters.franchise_id !== 'all') {
        params.franchise_id = expenseReportFilters.franchise_id;
      }
      const response = await axios.get(`${API}/franchise-expenses/all-reports`, { params });
      setFranchiseExpenseReports(response.data.reports || []);
    } catch (error) {
      console.error('Error fetching expense reports:', error);
      toast.error('Failed to fetch expense reports');
    }
  };

  const fetchMenuItems = async () => {
    try {
      const response = await axios.get(`${API}/menu-items`);
      const items = response.data.menu_items || [];
      setMenuItems(items);
      
      // Extract unique categories
      const uniqueCategories = [...new Set(items.map(item => item.category).filter(Boolean))];
      setCategories(uniqueCategories);
    } catch (error) {
      console.error('Error fetching menu items:', error);
    }
  };

  const fetchInventory = async () => {
    try {
      const response = await axios.get(`${API}/inventory`);
      setInventory(response.data.inventory || []);
    } catch (error) {
      console.error('Error fetching inventory:', error);
    }
  };

  const fetchFranchiseDetails = async (franchiseId) => {
    try {
      const response = await axios.get(`${API}/franchises/${franchiseId}`);
      setSelectedFranchise(response.data.franchise);
      setIsFranchiseDetailsOpen(true);
    } catch (error) {
      console.error('Error fetching franchise details:', error);
      toast.error('Failed to load franchise details');
    }
  };

  const fetchAllLedgers = async () => {
    try {
      const response = await axios.get(`${API}/ledger/parties`);
      setAllLedgers(response.data.parties || []);
    } catch (error) {
      console.error('Error fetching all ledgers:', error);
      toast.error('Failed to load ledgers');
    }
  };

  const viewPartyLedgerDetails = async (party) => {
    setSelectedPartyLedger(party);
    setIsPartyLedgerDetailsOpen(true);
    // Fetch transactions for this party
    try {
      const response = await axios.get(`${API}/ledger/transactions?party_id=${party.party_id}`);
      const transactions = response.data.transactions || [];
      setSelectedPartyLedger({ ...party, transactions });
    } catch (error) {
      console.error('Error fetching transactions:', error);
      toast.error('Failed to load transaction history');
    }
  };

  const handleGenerateActualsReport = async () => {
    try {
      const params = new URLSearchParams();
      if (actualsFilters.franchise_id && actualsFilters.franchise_id !== 'all') {
        params.append('franchise_id', actualsFilters.franchise_id);
      }
      if (actualsFilters.start_date) {
        params.append('start_date', actualsFilters.start_date);
      }
      if (actualsFilters.end_date) {
        params.append('end_date', actualsFilters.end_date);
      }

      const response = await axios.get(`${API}/daily-actuals?${params.toString()}`);
      setActualsData(response.data.actuals || []);
      toast.success('Actuals report generated successfully!');
    } catch (error) {
      console.error('Error generating actuals report:', error);
      toast.error('Failed to generate actuals report');
    }
  };

  const handleDownloadReportPDF = () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    
    // Get franchise details
    const selectedFranchiseData = franchises.find(f => f.id === reportFilters.franchise_id);
    const franchiseName = selectedFranchiseData ? selectedFranchiseData.name : 'All Franchises';
    const franchiseAddress = selectedFranchiseData ? selectedFranchiseData.franchise_address : '';
    const franchiseContact = selectedFranchiseData ? selectedFranchiseData.contact_number : '';
    
    // Header
    doc.setFontSize(20);
    doc.setFont(undefined, 'bold');
    doc.text('1946 Billing System', pageWidth / 2, 20, { align: 'center' });
    
    // Report Details
    doc.setFontSize(10);
    doc.setFont(undefined, 'normal');
    const now = new Date();
    doc.text(`Date: ${now.toLocaleDateString()}`, 14, 35);
    doc.text(`Time: ${now.toLocaleTimeString()}`, 14, 40);
    doc.text(`Report Period: ${reportFilters.start_date || 'All'} to ${reportFilters.end_date || 'All'}`, 14, 45);
    
    // Franchise Details
    doc.setFont(undefined, 'bold');
    doc.text('Franchise Details:', 14, 55);
    doc.setFont(undefined, 'normal');
    doc.text(`Name: ${franchiseName}`, 14, 60);
    if (franchiseAddress) doc.text(`Address: ${franchiseAddress}`, 14, 65);
    if (franchiseContact) doc.text(`Contact: ${franchiseContact}`, 14, 70);
    
    // Line separator
    doc.line(14, 75, pageWidth - 14, 75);
    
    // Sales Report Title
    doc.setFont(undefined, 'bold');
    doc.setFontSize(14);
    doc.text('Sales Report', 14, 85);
    
    // Table Headers
    doc.setFontSize(10);
    let yPosition = 95;
    doc.text('Item Name', 14, yPosition);
    doc.text('Price/Unit', 80, yPosition);
    doc.text('Quantity', 120, yPosition);
    doc.text('Total', 160, yPosition, { align: 'right' });
    
    // Line under headers
    doc.line(14, yPosition + 2, pageWidth - 14, yPosition + 2);
    yPosition += 10;
    
    // Table Data with Categories
    doc.setFont(undefined, 'normal');
    if (reportData && reportData.categories) {
      reportData.categories.forEach((category) => {
        if (yPosition > 270) {
          doc.addPage();
          yPosition = 20;
        }
        
        // Category header
        doc.setFont(undefined, 'bold');
        doc.setFontSize(11);
        doc.text(category.category.toUpperCase(), 14, yPosition);
        yPosition += 8;
        
        // Category items
        doc.setFont(undefined, 'normal');
        doc.setFontSize(10);
        category.items.forEach((item) => {
          if (yPosition > 270) {
            doc.addPage();
            yPosition = 20;
          }
          doc.text('  ' + item.name, 14, yPosition);
          doc.text(`Rs ${item.price}`, 80, yPosition);
          doc.text(`${item.quantity}`, 120, yPosition);
          doc.text(`Rs ${item.total.toFixed(2)}`, 160, yPosition, { align: 'right' });
          yPosition += 7;
        });
        
        // Category subtotal
        doc.setFont(undefined, 'bold');
        doc.text(`Subtotal (${category.category}):`, 14, yPosition);
        doc.text(`Rs ${category.subtotal.toFixed(2)}`, 160, yPosition, { align: 'right' });
        yPosition += 10;
      });
    }
    
    // Line above grand total
    doc.line(14, yPosition, pageWidth - 14, yPosition);
    yPosition += 8;
    
    // Grand Total
    doc.setFont(undefined, 'bold');
    doc.setFontSize(12);
    doc.text('Grand Total:', 14, yPosition);
    doc.text(`Rs ${reportData?.grand_total?.toFixed(2) || '0.00'}`, 160, yPosition, { align: 'right' });
    
    // Footer
    yPosition = doc.internal.pageSize.getHeight() - 30;
    doc.setFontSize(10);
    doc.setFont(undefined, 'italic');
    doc.text('Thank you for your business!', pageWidth / 2, yPosition, { align: 'center' });
    yPosition += 6;
    doc.setFontSize(8);
    doc.text('* This receipt is for your reference only', pageWidth / 2, yPosition, { align: 'center' });
    
    // Save PDF
    const fileName = `Sales_Report_${franchiseName.replace(/\s+/g, '_')}_${now.toISOString().split('T')[0]}.pdf`;
    doc.save(fileName);
    toast.success('Report downloaded successfully!');
  };

  const handleAddFranchise = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API}/franchises`, newFranchise);
      toast.success(`Franchise added successfully! PIN: ${response.data.pin}`, { duration: 5000 });
      setNewFranchise({
        franchise_name: '',
        franchise_model: 'Kiosk',
        franchise_amount_collected: '',
        owner_name: '',
        contact_number: '',
        aadhaar_no: '',
        pan_no: '',
        owner_address: '',
        franchise_address: '',
        city: '',
        pincode: '',
        district: '',
        state: '',
        taluk: '',
        date_of_opening: '',
        additional_owner_names: '',
        wifi_password: ''
      });
      setIsAddFranchiseOpen(false);
      fetchFranchises();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add franchise');
    }
  };

  const handleAddMenuItem = async (e) => {
    e.preventDefault();
    
    // Validate category is selected
    if (!newMenuItem.category) {
      toast.error('Please select a category');
      return;
    }
    
    try {
      const selectedFranchises = newMenuItem.selected_franchises.length > 0 
        ? newMenuItem.selected_franchises 
        : franchises.map(f => f.id);
      
      await axios.post(`${API}/menu-items`, { 
        name: newMenuItem.name,
        category: newMenuItem.category,
        price: parseFloat(newMenuItem.price),
        selected_franchises: selectedFranchises
      });
      toast.success('Menu item added successfully!');
      setNewMenuItem({ name: '', category: '', price: '', selected_franchises: [] });
      setIsAddMenuOpen(false);
      fetchMenuItems();
    } catch (error) {
      toast.error('Failed to add menu item');
    }
  };

  const handleEditMenuItem = async (e) => {
    e.preventDefault();
    
    // Validate category is selected
    if (!editMenuItem.category) {
      toast.error('Please select a category');
      return;
    }
    
    try {
      await axios.put(`${API}/menu-items/${editMenuItem.id}`, {
        name: editMenuItem.name,
        category: editMenuItem.category,
        price: parseFloat(editMenuItem.price),
        is_active: true,
        selected_franchises: editMenuItem.selected_franchises || []
      });
      toast.success('Menu item updated successfully!');
      setEditMenuItem(null);
      setIsEditMenuOpen(false);
      fetchMenuItems();
    } catch (error) {
      toast.error('Failed to update menu item');
    }
  };

  const handleDeleteMenuItem = async (itemId) => {
    if (!window.confirm('Are you sure you want to delete this menu item?')) {
      return;
    }
    try {
      await axios.delete(`${API}/menu-items/${itemId}`);
      toast.success('Menu item deleted successfully!');
      fetchMenuItems();
    } catch (error) {
      toast.error('Failed to delete menu item');
    }
  };

  const handleToggleFranchise = (franchiseId) => {
    setNewMenuItem(prev => {
      const selected = prev.selected_franchises || [];
      if (selected.includes(franchiseId)) {
        return { ...prev, selected_franchises: selected.filter(id => id !== franchiseId) };
      } else {
        return { ...prev, selected_franchises: [...selected, franchiseId] };
      }
    });
  };

  const handleSelectAllFranchises = () => {
    setNewMenuItem(prev => ({
      ...prev,
      selected_franchises: franchises.map(f => f.id)
    }));
  };

  const handleDeselectAllFranchises = () => {
    setNewMenuItem(prev => ({ ...prev, selected_franchises: [] }));
  };

  const handleAddInventory = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/inventory`, newInventory);
      toast.success('Inventory item added successfully!');
      setNewInventory({ item_name: '', unit: '' });
      setIsAddInventoryOpen(false);
      fetchInventory();
    } catch (error) {
      toast.error('Failed to add inventory item');
    }
  };

  const handleGenerateReport = async () => {
    try {
      const filters = {
        ...reportFilters,
        franchise_id: reportFilters.franchise_id === 'all' ? '' : reportFilters.franchise_id
      };
      const response = await axios.post(`${API}/reports/sales`, filters);
      setReportData(response.data);
      toast.success('Report generated successfully!');
    } catch (error) {
      toast.error('Failed to generate report');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('user');
    setUser(null);
    navigate('/');
  };

  const handleInitializeSheets = async () => {
    try {
      await axios.post(`${API}/initialize-sheets`);
      toast.success('Sheets initialized with sample data!');
      fetchMenuItems();
      fetchInventory();
    } catch (error) {
      toast.error('Failed to initialize sheets');
    }
  };

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  return (
    <div className="flex h-screen bg-background overflow-hidden">
      {/* Initial Loading Screen */}
      {initialLoading && (
        <div className="fixed inset-0 bg-background z-50 flex items-center justify-center">
          <LogoLoader size="lg" text="Loading Admin Dashboard..." />
        </div>
      )}
      
      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}
      
      {/* Sidebar - Responsive & Collapsible */}
      <div className={`
        fixed lg:relative inset-y-0 left-0 z-50
        ${isSidebarCollapsed ? 'w-16' : 'w-64'} bg-surface border-r border-border flex flex-col shadow-lg
        transform transition-all duration-300 ease-in-out
        ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        <div className={`${isSidebarCollapsed ? 'p-2' : 'p-6'} border-b border-border flex-shrink-0`}>
          <div className="flex justify-center mb-3">
            <img src="/logo.png" alt="1946 Logo" className={`${isSidebarCollapsed ? 'h-10' : 'h-16'} w-auto object-contain`} />
          </div>
          {!isSidebarCollapsed && (
            <>
              <h1 className="text-xl font-bold text-primary text-center">Admin Portal</h1>
              <p className="text-sm text-secondary mt-1 text-center">HQ Dashboard</p>
            </>
          )}
        </div>
        <nav className={`flex-1 ${isSidebarCollapsed ? 'p-2' : 'p-4'} space-y-2 overflow-y-auto`}>
          <Button
            data-testid="franchises-nav"
            variant={activeSection === 'franchises' ? 'default' : 'ghost'}
            className={`w-full ${isSidebarCollapsed ? 'justify-center px-2' : 'justify-start'} h-12 rounded-xl font-medium`}
            onClick={() => setActiveSection('franchises')}
            title="Franchises"
          >
            <Users className={`${isSidebarCollapsed ? '' : 'mr-3'} h-5 w-5`} />
            {!isSidebarCollapsed && 'Franchises'}
          </Button>
          <Button
            data-testid="menu-nav"
            variant={activeSection === 'menu' ? 'default' : 'ghost'}
            className={`w-full ${isSidebarCollapsed ? 'justify-center px-2' : 'justify-start'} h-12 rounded-xl font-medium`}
            onClick={() => setActiveSection('menu')}
            title="Menu Items"
          >
            <MenuIcon className={`${isSidebarCollapsed ? '' : 'mr-3'} h-5 w-5`} />
            {!isSidebarCollapsed && 'Menu Items'}
          </Button>
          <Button
            data-testid="inventory-nav"
            variant={activeSection === 'inventory' ? 'default' : 'ghost'}
            className={`w-full ${isSidebarCollapsed ? 'justify-center px-2' : 'justify-start'} h-12 rounded-xl font-medium`}
            onClick={() => setActiveSection('inventory')}
            title="Inventory"
          >
            <Package className={`${isSidebarCollapsed ? '' : 'mr-3'} h-5 w-5`} />
            {!isSidebarCollapsed && 'Inventory'}
          </Button>
          <Button
            data-testid="reports-nav"
            variant={activeSection === 'reports' ? 'default' : 'ghost'}
            className={`w-full ${isSidebarCollapsed ? 'justify-center px-2' : 'justify-start'} h-12 rounded-xl font-medium`}
            onClick={() => setActiveSection('reports')}
            title="Sales Reports"
          >
            <BarChart className={`${isSidebarCollapsed ? '' : 'mr-3'} h-5 w-5`} />
            {!isSidebarCollapsed && 'Sales Reports'}
          </Button>
          <Button
            data-testid="franchise-expenses-nav"
            variant={activeSection === 'franchise-expenses' ? 'default' : 'ghost'}
            className={`w-full ${isSidebarCollapsed ? 'justify-center px-2' : 'justify-start'} h-12 rounded-xl font-medium`}
            onClick={() => setActiveSection('franchise-expenses')}
            title="Franchise Expenses"
          >
            <Receipt className={`${isSidebarCollapsed ? '' : 'mr-3'} h-5 w-5`} />
            {!isSidebarCollapsed && 'Franchise Expenses'}
          </Button>
          <Button
            data-testid="actuals-nav"
            variant={activeSection === 'actuals' ? 'default' : 'ghost'}
            className={`w-full ${isSidebarCollapsed ? 'justify-center px-2' : 'justify-start'} h-12 rounded-xl font-medium`}
            onClick={() => setActiveSection('actuals')}
            title="Franchise Actuals"
          >
            <FileText className={`${isSidebarCollapsed ? '' : 'mr-3'} h-5 w-5`} />
            {!isSidebarCollapsed && 'Franchise Actuals'}
          </Button>
          <Button
            data-testid="ledgers-nav"
            variant={activeSection === 'ledgers' ? 'default' : 'ghost'}
            className={`w-full ${isSidebarCollapsed ? 'justify-center px-2' : 'justify-start'} h-12 rounded-xl font-medium`}
            onClick={() => setActiveSection('ledgers')}
            title="Party Ledgers"
          >
            <Wallet className={`${isSidebarCollapsed ? '' : 'mr-3'} h-5 w-5`} />
            {!isSidebarCollapsed && 'Party Ledgers'}
          </Button>
          <Button
            data-testid="setup-nav"
            variant={activeSection === 'setup' ? 'default' : 'ghost'}
            className={`w-full ${isSidebarCollapsed ? 'justify-center px-2' : 'justify-start'} h-12 rounded-xl font-medium`}
            onClick={() => setActiveSection('setup')}
            title="Setup"
          >
            <Settings className={`${isSidebarCollapsed ? '' : 'mr-3'} h-5 w-5`} />
            {!isSidebarCollapsed && 'Setup'}
          </Button>
          
          {/* Divider for Raw Materials */}
          <div className="my-3 border-t border-border" />
          
          {/* Raw Materials Section */}
          <Button
            data-testid="raw-materials-nav"
            variant={activeSection === 'raw-materials' ? 'default' : 'ghost'}
            className={`w-full ${isSidebarCollapsed ? 'justify-center px-2' : 'justify-start'} h-12 rounded-xl font-medium bg-emerald-50 hover:bg-emerald-100 border border-emerald-200`}
            onClick={() => setActiveSection('raw-materials')}
            title="Raw Materials"
          >
            <Package className={`${isSidebarCollapsed ? '' : 'mr-3'} h-5 w-5 text-emerald-700`} />
            {!isSidebarCollapsed && <span className="text-emerald-800">Raw Materials</span>}
          </Button>
          <Button
            data-testid="raw-material-orders-nav"
            variant={activeSection === 'raw-material-orders' ? 'default' : 'ghost'}
            className={`w-full ${isSidebarCollapsed ? 'justify-center px-2' : 'justify-start'} h-12 rounded-xl font-medium bg-emerald-50 hover:bg-emerald-100 border border-emerald-200`}
            onClick={() => setActiveSection('raw-material-orders')}
            title="RM Orders"
          >
            <Receipt className={`${isSidebarCollapsed ? '' : 'mr-3'} h-5 w-5 text-emerald-700`} />
            {!isSidebarCollapsed && <span className="text-emerald-800">RM Orders</span>}
          </Button>
          
          {/* Divider */}
          <div className="my-3 border-t border-border" />
          
          {/* Cafe 1946 - Separate Feature */}
          <Button
            data-testid="cafe-nav"
            variant={activeSection === 'cafe' ? 'default' : 'ghost'}
            className={`w-full ${isSidebarCollapsed ? 'justify-center px-2' : 'justify-start'} h-12 rounded-xl font-medium bg-amber-50 hover:bg-amber-100 border border-amber-200`}
            onClick={() => setActiveSection('cafe')}
            title="Cafe 1946"
          >
            <Coffee className={`${isSidebarCollapsed ? '' : 'mr-3'} h-5 w-5 text-amber-700`} />
            {!isSidebarCollapsed && <span className="text-amber-800">Cafe 1946</span>}
          </Button>
        </nav>
        
        {/* Collapse Toggle Button */}
        <div className={`${isSidebarCollapsed ? 'p-2' : 'p-4'} border-t border-border flex-shrink-0 hidden lg:block`}>
          <Button
            variant="ghost"
            className={`w-full ${isSidebarCollapsed ? 'justify-center px-2' : 'justify-start'} h-10 rounded-xl`}
            onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
            title={isSidebarCollapsed ? 'Expand Sidebar' : 'Collapse Sidebar'}
          >
            {isSidebarCollapsed ? (
              <PanelLeft className="h-5 w-5" />
            ) : (
              <>
                <PanelLeftClose className="mr-3 h-5 w-5" />
                Collapse
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Main Content - Responsive */}
      <div className="flex-1 overflow-auto">
        {/* Header Bar - All Screens */}
        <div className="sticky top-0 z-30 bg-surface border-b border-border">
          <div className="px-4 py-3 flex items-center justify-between">
            {/* Mobile Menu Button */}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsMobileMenuOpen(true)}
              className="p-2 lg:hidden"
            >
              <MenuIcon className="h-6 w-6" />
            </Button>
            
            {/* Logo & Title (Mobile Only) */}
            <div className="flex items-center gap-2 lg:hidden">
              <img src="/logo.png" alt="Logo" className="h-8 w-auto" />
              <h1 className="text-lg font-bold text-primary">Admin</h1>
            </div>

            {/* Spacer for desktop */}
            <div className="hidden lg:block flex-1"></div>
            
            {/* Logout Button - Always Visible */}
            <Button
              data-testid="admin-logout-button"
              variant="ghost"
              size="sm"
              onClick={handleLogout}
              className="p-2 hover:bg-red-50 rounded-full transition-colors group"
              title="Logout"
            >
              <LogOut className="h-5 w-5 text-secondary group-hover:text-red-600" />
            </Button>
          </div>
        </div>

        <div className="p-4 sm:p-6 lg:p-8">
        {activeSection === 'franchises' && (
          <div>
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
              <h2 className="text-2xl sm:text-3xl font-bold text-primary">Franchises</h2>
              <Dialog open={isAddFranchiseOpen} onOpenChange={setIsAddFranchiseOpen}>
                <DialogTrigger asChild>
                  <Button className="h-10 sm:h-12 bg-primary text-white hover:bg-primary/90 rounded-full px-4 sm:px-6 font-bold text-sm sm:text-base uppercase tracking-wider w-full sm:w-auto">
                    Add Franchise
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-surface max-w-3xl max-h-[85vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle className="text-2xl font-bold text-primary">Add New Franchise</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleAddFranchise} className="space-y-6 mt-4">
                    {/* Basic Information */}
                    <div className="space-y-4">
                      <h3 className="font-semibold text-lg text-primary border-b pb-2">Basic Information</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="franchise_name">Franchise Name *</Label>
                          <Input
                            id="franchise_name"
                            value={newFranchise.franchise_name}
                            onChange={(e) => setNewFranchise({ ...newFranchise, franchise_name: e.target.value })}
                            placeholder="e.g., 1946 Coffee - MG Road"
                            className="h-10"
                            required
                          />
                        </div>
                        <div>
                          <Label htmlFor="franchise_model">Franchise Model *</Label>
                          <Select
                            value={newFranchise.franchise_model}
                            onValueChange={(value) => setNewFranchise({ ...newFranchise, franchise_model: value })}
                          >
                            <SelectTrigger className="h-10">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="Kiosk">Kiosk</SelectItem>
                              <SelectItem value="Cafe">Cafe</SelectItem>
                              <SelectItem value="Cloud Kitchen">Cloud Kitchen</SelectItem>
                              <SelectItem value="Express">Express</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div>
                        <Label htmlFor="franchise_amount_collected">Franchise Amount Collected *</Label>
                        <Input
                          id="franchise_amount_collected"
                          type="number"
                          value={newFranchise.franchise_amount_collected}
                          onChange={(e) => setNewFranchise({ ...newFranchise, franchise_amount_collected: e.target.value })}
                          placeholder="e.g., 400000"
                          className="h-10"
                          required
                        />
                      </div>
                      <div>
                        <Label htmlFor="date_of_opening">Date of Opening *</Label>
                        <Input
                          id="date_of_opening"
                          type="date"
                          value={newFranchise.date_of_opening}
                          onChange={(e) => setNewFranchise({ ...newFranchise, date_of_opening: e.target.value })}
                          className="h-10"
                          required
                        />
                      </div>
                    </div>

                    {/* Owner Details */}
                    <div className="space-y-4">
                      <h3 className="font-semibold text-lg text-primary border-b pb-2">Owner Details</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="owner_name">Primary Owner Name *</Label>
                          <Input
                            id="owner_name"
                            value={newFranchise.owner_name}
                            onChange={(e) => setNewFranchise({ ...newFranchise, owner_name: e.target.value })}
                            placeholder="Full name"
                            className="h-10"
                            required
                          />
                        </div>
                        <div>
                          <Label htmlFor="contact_number">Contact Number *</Label>
                          <Input
                            id="contact_number"
                            type="tel"
                            value={newFranchise.contact_number}
                            onChange={(e) => setNewFranchise({ ...newFranchise, contact_number: e.target.value })}
                            placeholder="10-digit mobile number"
                            pattern="[0-9]{10}"
                            className="h-10"
                            required
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="aadhaar_no">Aadhaar Number *</Label>
                          <Input
                            id="aadhaar_no"
                            value={newFranchise.aadhaar_no}
                            onChange={(e) => setNewFranchise({ ...newFranchise, aadhaar_no: e.target.value })}
                            placeholder="12-digit Aadhaar"
                            pattern="[0-9]{12}"
                            className="h-10"
                            required
                          />
                        </div>
                        <div>
                          <Label htmlFor="pan_no">PAN Number</Label>
                          <Input
                            id="pan_no"
                            value={newFranchise.pan_no}
                            onChange={(e) => setNewFranchise({ ...newFranchise, pan_no: e.target.value.toUpperCase() })}
                            placeholder="e.g., ABCDE1234F"
                            pattern="[A-Z]{5}[0-9]{4}[A-Z]{1}"
                            className="h-10"
                          />
                        </div>
                      </div>
                      <div>
                        <Label htmlFor="owner_address">Owner's Address *</Label>
                        <Input
                          id="owner_address"
                          value={newFranchise.owner_address}
                          onChange={(e) => setNewFranchise({ ...newFranchise, owner_address: e.target.value })}
                          placeholder="Residential address"
                          className="h-10"
                          required
                        />
                      </div>
                      <div>
                        <Label htmlFor="additional_owner_names">Additional Owners</Label>
                        <Input
                          id="additional_owner_names"
                          value={newFranchise.additional_owner_names}
                          onChange={(e) => setNewFranchise({ ...newFranchise, additional_owner_names: e.target.value })}
                          placeholder="Comma-separated names"
                          className="h-10"
                        />
                      </div>
                    </div>

                    {/* Franchise Location */}
                    <div className="space-y-4">
                      <h3 className="font-semibold text-lg text-primary border-b pb-2">Franchise Location</h3>
                      <div>
                        <Label htmlFor="franchise_address">Franchise Address *</Label>
                        <Input
                          id="franchise_address"
                          value={newFranchise.franchise_address}
                          onChange={(e) => setNewFranchise({ ...newFranchise, franchise_address: e.target.value })}
                          placeholder="Business location address"
                          className="h-10"
                          required
                        />
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="city">City *</Label>
                          <Input
                            id="city"
                            value={newFranchise.city}
                            onChange={(e) => setNewFranchise({ ...newFranchise, city: e.target.value })}
                            placeholder="City name"
                            className="h-10"
                            required
                          />
                        </div>
                        <div>
                          <Label htmlFor="pincode">Pincode *</Label>
                          <Input
                            id="pincode"
                            value={newFranchise.pincode}
                            onChange={(e) => setNewFranchise({ ...newFranchise, pincode: e.target.value })}
                            placeholder="6-digit pincode"
                            pattern="[0-9]{6}"
                            className="h-10"
                            required
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <Label htmlFor="taluk">Taluk *</Label>
                          <Input
                            id="taluk"
                            value={newFranchise.taluk}
                            onChange={(e) => setNewFranchise({ ...newFranchise, taluk: e.target.value })}
                            placeholder="Taluk"
                            className="h-10"
                            required
                          />
                        </div>
                        <div>
                          <Label htmlFor="district">District *</Label>
                          <Input
                            id="district"
                            value={newFranchise.district}
                            onChange={(e) => setNewFranchise({ ...newFranchise, district: e.target.value })}
                            placeholder="District"
                            className="h-10"
                            required
                          />
                        </div>
                        <div>
                          <Label htmlFor="state">State *</Label>
                          <Input
                            id="state"
                            value={newFranchise.state}
                            onChange={(e) => setNewFranchise({ ...newFranchise, state: e.target.value })}
                            placeholder="State"
                            className="h-10"
                            required
                          />
                        </div>
                      </div>
                    </div>

                    {/* Additional Information */}
                    <div className="space-y-4">
                      <h3 className="font-semibold text-lg text-primary border-b pb-2">Additional Information</h3>
                      <div>
                        <Label htmlFor="wifi_password">WiFi Password</Label>
                        <Input
                          id="wifi_password"
                          type="text"
                          value={newFranchise.wifi_password}
                          onChange={(e) => setNewFranchise({ ...newFranchise, wifi_password: e.target.value })}
                          placeholder="Franchise WiFi password"
                          className="h-10"
                        />
                      </div>
                    </div>

                    <div className="flex gap-3 pt-4">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setIsAddFranchiseOpen(false)}
                        className="flex-1 h-12 rounded-full font-bold"
                      >
                        Cancel
                      </Button>
                      <Button
                        type="submit"
                        className="flex-1 h-12 bg-accent text-white hover:bg-accent/90 rounded-full font-bold"
                      >
                        Create Franchise
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
              {franchises.map((franchise) => (
                <Card key={franchise.id} data-testid={`franchise-card-${franchise.id}`} className="shadow-sm border-border hover:shadow-md transition-shadow">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg sm:text-xl font-bold text-primary truncate" title={franchise.name}>
                      {franchise.name}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-1.5">
                    <p className="text-xs sm:text-sm text-secondary truncate">
                      <span className="font-medium">ID:</span> {franchise.id}
                    </p>
                    <p className="text-xs sm:text-sm text-secondary">
                      <span className="font-medium">Location:</span> {franchise.location}
                    </p>
                    <p className="text-xs sm:text-sm text-secondary">
                      <span className="font-medium">Contact:</span> {franchise.contact_person}
                    </p>
                    <p className="text-xs sm:text-sm text-secondary">
                      <span className="font-medium">Phone:</span> {franchise.contact_number}
                    </p>
                    <p className="text-xs sm:text-sm text-secondary mb-3">
                      <span className="font-medium">PIN:</span> {franchise.pin_code || franchise.id.slice(-5)}
                    </p>
                    <div className="flex flex-col sm:flex-row gap-2 pt-2">
                      <Button
                        onClick={() => fetchFranchiseDetails(franchise.id)}
                        className="flex-1 h-9 sm:h-10 bg-accent text-white hover:bg-accent/90 rounded-full font-bold text-xs sm:text-sm"
                      >
                        View Details
                      </Button>
                      <Button
                        onClick={() => {
                          setSelectedFranchise(franchise);
                          setIsDeviceInfoOpen(true);
                        }}
                        variant="outline"
                        className="h-9 sm:h-10 px-3 bg-blue-50 hover:bg-blue-100 border-blue-200 rounded-full sm:w-auto"
                        title="View Device Info"
                      >
                        <Smartphone className="h-4 w-4" />
                        <span className="ml-2 sm:hidden">Device Info</span>
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {activeSection === 'menu' && (
          <div>
            <div className="flex flex-col gap-4 mb-6">
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
                <h2 className="text-2xl sm:text-3xl font-bold text-primary">Menu Items</h2>
                <Dialog open={isAddMenuOpen} onOpenChange={setIsAddMenuOpen}>
                  <DialogTrigger asChild>
                    <Button data-testid="add-menu-item-button" className="h-10 sm:h-12 bg-primary text-white hover:bg-primary/90 rounded-full px-4 sm:px-6 font-bold text-sm sm:text-base uppercase tracking-wider w-full sm:w-auto">
                      Add Menu Item
                    </Button>
                  </DialogTrigger>
                <DialogContent className="bg-surface">
                  <DialogHeader>
                    <DialogTitle className="text-2xl font-bold">Add New Menu Item</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleAddMenuItem} className="space-y-4 mt-4">
                    <div>
                      <Label htmlFor="menu-name">Name</Label>
                      <Input
                        id="menu-name"
                        data-testid="menu-name-input"
                        value={newMenuItem.name}
                        onChange={(e) => setNewMenuItem({ ...newMenuItem, name: e.target.value })}
                        className="h-12 rounded-xl"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="menu-category">Category <span className="text-red-500">*</span></Label>
                      <Button
                        type="button"
                        variant="outline"
                        className="w-full h-12 rounded-xl justify-between"
                        onClick={() => {
                          setCategoryModalFor('new');
                          setIsCategoryModalOpen(true);
                        }}
                      >
                        <span className={newMenuItem.category ? "text-primary" : "text-gray-500"}>
                          {newMenuItem.category || "Select category"}
                        </span>
                        <MenuIcon className="h-4 w-4" />
                      </Button>
                    </div>
                    <div>
                      <Label htmlFor="menu-price">Price (₹)</Label>
                      <Input
                        id="menu-price"
                        data-testid="menu-price-input"
                        type="number"
                        step="0.01"
                        value={newMenuItem.price}
                        onChange={(e) => setNewMenuItem({ ...newMenuItem, price: e.target.value })}
                        className="h-12 rounded-xl"
                        required
                      />
                    </div>
                    <div>
                      <div className="flex justify-between items-center mb-2">
                        <Label>Assign to Franchises</Label>
                        <div className="space-x-2">
                          <Button type="button" variant="ghost" size="sm" onClick={handleSelectAllFranchises}>Select All</Button>
                          <Button type="button" variant="ghost" size="sm" onClick={handleDeselectAllFranchises}>Deselect All</Button>
                        </div>
                      </div>
                      <div className="max-h-48 overflow-y-auto border rounded-xl p-3 space-y-2">
                        {franchises.map((franchise) => (
                          <label key={franchise.id} className="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={(newMenuItem.selected_franchises || []).includes(franchise.id)}
                              onChange={() => handleToggleFranchise(franchise.id)}
                              className="w-4 h-4 rounded"
                            />
                            <span className="text-sm">{franchise.name}</span>
                          </label>
                        ))}
                      </div>
                      <p className="text-xs text-secondary mt-2">
                        {newMenuItem.selected_franchises.length === 0 
                          ? "All franchises selected by default" 
                          : `${newMenuItem.selected_franchises.length} franchise(s) selected`}
                      </p>
                    </div>
                    <Button type="submit" data-testid="submit-menu-item-button" className="w-full h-12 bg-primary text-white hover:bg-primary/90 rounded-full font-bold uppercase tracking-wider">
                      Add Menu Item
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
              </div>
              {/* Search Input */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  type="text"
                  placeholder="Search menu items by name or category..."
                  value={menuSearchQuery}
                  onChange={(e) => setMenuSearchQuery(e.target.value)}
                  className="pl-10 h-11 rounded-xl"
                />
              </div>
            </div>
            <div className="grid grid-cols-1 xs:grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 sm:gap-4">
              {/* Sort menu items alphabetically by category, then by name, and filter by search */}
              {[...menuItems]
                .sort((a, b) => {
                  const categoryCompare = (a.category || '').localeCompare(b.category || '');
                  if (categoryCompare !== 0) return categoryCompare;
                  return (a.name || '').localeCompare(b.name || '');
                })
                .filter(item => {
                  if (!menuSearchQuery.trim()) return true;
                  const query = menuSearchQuery.toLowerCase();
                  return (
                    (item.name || '').toLowerCase().includes(query) ||
                    (item.category || '').toLowerCase().includes(query)
                  );
                })
                .map((item) => (
                <Card key={item.id} data-testid={`menu-item-card-${item.id}`} className="shadow-sm border-border hover:shadow-md transition-shadow">
                  <CardContent className="p-4">
                    <h3 className="font-bold text-primary mb-2">{item.name}</h3>
                    <p className="text-xs text-secondary uppercase tracking-wider mb-2">{item.category}</p>
                    <p className="text-2xl font-bold text-accent mb-3">₹{item.price}</p>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        className="flex-1"
                        onClick={() => {
                          // Convert franchises string to selected_franchises array
                          const franchisesStr = item.franchises || '';
                          const selectedFranchises = franchisesStr ? franchisesStr.split(',').filter(f => f.trim()) : [];
                          setEditMenuItem({
                            ...item,
                            selected_franchises: selectedFranchises
                          });
                          setIsEditMenuOpen(true);
                        }}
                      >
                        Edit
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        className="flex-1"
                        onClick={() => handleDeleteMenuItem(item.id)}
                      >
                        Delete
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Edit Menu Item Dialog */}
            <Dialog open={isEditMenuOpen} onOpenChange={setIsEditMenuOpen}>
              <DialogContent className="bg-surface">
                <DialogHeader>
                  <DialogTitle className="text-2xl font-bold">Edit Menu Item</DialogTitle>
                </DialogHeader>
                {editMenuItem && (
                  <form onSubmit={handleEditMenuItem} className="space-y-4 mt-4">
                    <div>
                      <Label htmlFor="edit-menu-name">Name</Label>
                      <Input
                        id="edit-menu-name"
                        value={editMenuItem.name}
                        onChange={(e) => setEditMenuItem({ ...editMenuItem, name: e.target.value })}
                        className="h-12 rounded-xl"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="edit-menu-category">Category <span className="text-red-500">*</span></Label>
                      <Button
                        type="button"
                        variant="outline"
                        className="w-full h-12 rounded-xl justify-between"
                        onClick={() => {
                          setCategoryModalFor('edit');
                          setIsCategoryModalOpen(true);
                        }}
                      >
                        <span className={editMenuItem.category ? "text-primary" : "text-gray-500"}>
                          {editMenuItem.category || "Select category"}
                        </span>
                        <MenuIcon className="h-4 w-4" />
                      </Button>
                    </div>
                    <div>
                      <Label htmlFor="edit-menu-price">Price (₹)</Label>
                      <Input
                        id="edit-menu-price"
                        type="number"
                        step="0.01"
                        value={editMenuItem.price}
                        onChange={(e) => setEditMenuItem({ ...editMenuItem, price: e.target.value })}
                        className="h-12 rounded-xl"
                        required
                      />
                    </div>
                    <div>
                      <Label>Assign to Franchises</Label>
                      <div className="max-h-48 overflow-y-auto border border-border rounded-xl p-4 mt-2">
                        {franchises.map((franchise) => (
                          <div key={franchise.id} className="flex items-center space-x-2 mb-2">
                            <input
                              type="checkbox"
                              id={`edit-franchise-${franchise.id}`}
                              checked={editMenuItem.selected_franchises?.includes(franchise.id) || false}
                              onChange={(e) => {
                                const selected = editMenuItem.selected_franchises || [];
                                if (e.target.checked) {
                                  setEditMenuItem({
                                    ...editMenuItem,
                                    selected_franchises: [...selected, franchise.id]
                                  });
                                } else {
                                  setEditMenuItem({
                                    ...editMenuItem,
                                    selected_franchises: selected.filter(id => id !== franchise.id)
                                  });
                                }
                              }}
                              className="h-4 w-4"
                            />
                            <label htmlFor={`edit-franchise-${franchise.id}`} className="text-sm">
                              {franchise.name}
                            </label>
                          </div>
                        ))}
                      </div>
                    </div>
                    <Button type="submit" className="w-full h-12 bg-primary text-white hover:bg-primary/90 rounded-full font-bold uppercase tracking-wider">
                      Update Menu Item
                    </Button>
                  </form>
                )}
              </DialogContent>
            </Dialog>
          </div>
        )}

        {activeSection === 'inventory' && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-3xl font-bold text-primary">Inventory</h2>
              <Dialog open={isAddInventoryOpen} onOpenChange={setIsAddInventoryOpen}>
                <DialogTrigger asChild>
                  <Button data-testid="add-inventory-button" className="h-12 bg-primary text-white hover:bg-primary/90 rounded-full px-6 font-bold uppercase tracking-wider">
                    Add Inventory Item
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-surface">
                  <DialogHeader>
                    <DialogTitle className="text-2xl font-bold">Add New Inventory Item</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleAddInventory} className="space-y-4 mt-4">
                    <div>
                      <Label htmlFor="inventory-name">Item Name</Label>
                      <Input
                        id="inventory-name"
                        data-testid="inventory-name-input"
                        value={newInventory.item_name}
                        onChange={(e) => setNewInventory({ ...newInventory, item_name: e.target.value })}
                        className="h-12 rounded-xl"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="inventory-unit">Unit</Label>
                      <Input
                        id="inventory-unit"
                        data-testid="inventory-unit-input"
                        value={newInventory.unit}
                        onChange={(e) => setNewInventory({ ...newInventory, unit: e.target.value })}
                        className="h-12 rounded-xl"
                        placeholder="e.g., Kg, Liters, Packets"
                        required
                      />
                    </div>
                    <Button type="submit" data-testid="submit-inventory-button" className="w-full h-12 bg-primary text-white hover:bg-primary/90 rounded-full font-bold uppercase tracking-wider">
                      Add Inventory Item
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {inventory.map((item) => (
                <Card key={item.id} data-testid={`inventory-item-card-${item.id}`} className="shadow-sm border-border">
                  <CardContent className="p-6">
                    <h3 className="font-bold text-lg text-primary mb-2">{item.item_name}</h3>
                    <p className="text-sm text-secondary">Unit: {item.unit}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {activeSection === 'reports' && (
          <div>
            <h2 className="text-3xl font-bold text-primary mb-6">Sales Reports</h2>
            <Card className="mb-6 shadow-sm border-border">
              <CardContent className="p-6">
                {/* Quick Date Filters */}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
                  <Button
                    variant="outline"
                    onClick={() => {
                      const today = new Date().toISOString().split('T')[0];
                      setReportFilters({ ...reportFilters, start_date: today, end_date: today });
                    }}
                    className="h-10 rounded-full font-bold"
                  >
                    Today
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      const yesterday = new Date();
                      yesterday.setDate(yesterday.getDate() - 1);
                      const dateStr = yesterday.toISOString().split('T')[0];
                      setReportFilters({ ...reportFilters, start_date: dateStr, end_date: dateStr });
                    }}
                    className="h-10 rounded-full font-bold"
                  >
                    Yesterday
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      const today = new Date();
                      const last7Days = new Date();
                      last7Days.setDate(today.getDate() - 7);
                      setReportFilters({
                        ...reportFilters,
                        start_date: last7Days.toISOString().split('T')[0],
                        end_date: today.toISOString().split('T')[0]
                      });
                    }}
                    className="h-10 rounded-full font-bold"
                  >
                    Last 7 Days
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      const today = new Date();
                      const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
                      setReportFilters({
                        ...reportFilters,
                        start_date: firstDay.toISOString().split('T')[0],
                        end_date: today.toISOString().split('T')[0]
                      });
                    }}
                    className="h-10 rounded-full font-bold"
                  >
                    This Month
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setReportFilters({ franchise_id: 'all', start_date: '', end_date: '' })}
                    className="h-10 rounded-full font-bold"
                  >
                    Clear
                  </Button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  <div>
                    <Label htmlFor="report-franchise">Franchise</Label>
                    <div className="relative">
                      <button
                        type="button"
                        onClick={() => setReportFilters({ ...reportFilters, franchise_id: 'all' })}
                        className={`absolute right-2 top-2 z-20 text-xs px-2 py-1 rounded-full font-medium transition-colors ${
                          reportFilters.franchise_id === 'all' 
                            ? 'bg-accent text-white' 
                            : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                        }`}
                      >
                        All
                      </button>
                      <SearchableDropdown
                        options={franchises}
                        value={reportFilters.franchise_id === 'all' ? '' : reportFilters.franchise_id}
                        onChange={(value) => setReportFilters({ ...reportFilters, franchise_id: value || 'all' })}
                        placeholder="Search franchise by name or city..."
                        getOptionLabel={(f) => `${f.name} - ${f.location}`}
                        getOptionValue={(f) => f.id}
                        searchKeys={['name', 'location', 'id']}
                        className="relative"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="report-start-date">Start Date</Label>
                    <Input
                      id="report-start-date"
                      data-testid="report-start-date-input"
                      type="date"
                      value={reportFilters.start_date}
                      onChange={(e) => setReportFilters({ ...reportFilters, start_date: e.target.value })}
                      className="h-12 rounded-xl"
                    />
                  </div>
                  <div>
                    <Label htmlFor="report-end-date">End Date</Label>
                    <Input
                      id="report-end-date"
                      data-testid="report-end-date-input"
                      type="date"
                      value={reportFilters.end_date}
                      onChange={(e) => setReportFilters({ ...reportFilters, end_date: e.target.value })}
                      className="h-12 rounded-xl"
                    />
                  </div>
                </div>
                <Button data-testid="generate-report-button" onClick={handleGenerateReport} className="w-full h-12 bg-primary text-white hover:bg-primary/90 rounded-full font-bold uppercase tracking-wider">
                  Generate Report
                </Button>
              </CardContent>
            </Card>

            {reportData && (
              <Card className="shadow-sm border-border">
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="text-xl font-bold text-primary">Sales Report</CardTitle>
                  <Button
                    onClick={handleDownloadReportPDF}
                    className="h-10 bg-accent text-white hover:bg-accent/90 rounded-full px-4 font-bold flex items-center gap-2"
                  >
                    <Download className="h-4 w-4" />
                    Download PDF
                  </Button>
                </CardHeader>
                <CardContent>
                  {reportData.categories && reportData.categories.length > 0 ? (
                    <div className="space-y-6 mb-6">
                      {reportData.categories.map((category, catIndex) => (
                        <div key={catIndex} className="space-y-3">
                          <h4 className="text-lg font-bold text-primary uppercase tracking-wide border-b-2 border-accent pb-2">
                            {category.category}
                          </h4>
                          {category.items.map((item, itemIndex) => (
                            <div key={itemIndex} className="flex justify-between items-center p-3 bg-muted rounded-lg ml-4">
                              <div className="flex-1">
                                <p className="font-bold text-primary">{item.name}</p>
                                <p className="text-sm text-secondary">₹{item.price}/unit × {item.quantity} units</p>
                              </div>
                              <p className="text-xl font-bold text-accent">₹{item.total.toFixed(2)}</p>
                            </div>
                          ))}
                          <div className="flex justify-between items-center p-3 bg-accent/10 rounded-lg ml-4 border-l-4 border-accent">
                            <p className="font-bold text-primary">Subtotal ({category.category})</p>
                            <p className="text-lg font-bold text-accent">₹{category.subtotal.toFixed(2)}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-center text-secondary py-8">No sales data for selected period</p>
                  )}
                  <div className="border-t-2 border-primary pt-4">
                    <div className="flex justify-between items-center">
                      <p className="text-xl font-bold text-primary">Grand Total:</p>
                      <p className="text-3xl font-bold text-accent" data-testid="report-grand-total">
                        ₹{reportData.grand_total ? reportData.grand_total.toFixed(2) : '0.00'}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Franchise Expenses Section */}
        {activeSection === 'franchise-expenses' && (
          <div className="space-y-6">
            <h2 className="text-3xl font-bold text-primary">Franchise Expenses</h2>
            
            {/* Expense Categories Management */}
            <Card className="shadow-sm border-border">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Settings className="h-5 w-5" />
                  Manage Expense Categories
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex gap-3 mb-4">
                  <Input
                    placeholder="New category name..."
                    value={newExpenseCategory}
                    onChange={(e) => setNewExpenseCategory(e.target.value)}
                    className="max-w-xs"
                    onKeyDown={(e) => e.key === 'Enter' && handleAddExpenseCategory()}
                  />
                  <Button onClick={handleAddExpenseCategory} className="bg-amber-600 hover:bg-amber-700">
                    <Plus className="h-4 w-4 mr-2" />
                    Add Category
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {expenseCategories.length === 0 ? (
                    <p className="text-secondary text-sm">No expense categories. Add some above.</p>
                  ) : (
                    expenseCategories.map(cat => (
                      <div key={cat.category_id} className="flex items-center gap-2 bg-muted px-3 py-2 rounded-full">
                        <span className="text-sm font-medium">{cat.name}</span>
                        <button
                          onClick={() => handleDeleteExpenseCategory(cat.category_id)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Franchise Expense Reports */}
            <Card className="shadow-sm border-border">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart className="h-5 w-5" />
                  Franchise Expense Reports
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                  <div>
                    <Label>Franchise</Label>
                    <Select
                      value={expenseReportFilters.franchise_id}
                      onValueChange={(value) => setExpenseReportFilters(prev => ({ ...prev, franchise_id: value }))}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="Select franchise" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Franchises</SelectItem>
                        {franchises.map(f => (
                          <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Start Date</Label>
                    <Input
                      type="date"
                      value={expenseReportFilters.start_date}
                      onChange={(e) => setExpenseReportFilters(prev => ({ ...prev, start_date: e.target.value }))}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>End Date</Label>
                    <Input
                      type="date"
                      value={expenseReportFilters.end_date}
                      onChange={(e) => setExpenseReportFilters(prev => ({ ...prev, end_date: e.target.value }))}
                      className="mt-1"
                    />
                  </div>
                  <div className="flex items-end">
                    <Button onClick={fetchFranchiseExpenseReports} className="w-full bg-amber-600 hover:bg-amber-700">
                      Generate Report
                    </Button>
                  </div>
                </div>

                {franchiseExpenseReports.length > 0 ? (
                  <div className="space-y-4">
                    {franchiseExpenseReports.map((report, index) => (
                      <Card key={index} className="border">
                        <CardHeader className="py-3 bg-muted/50">
                          <CardTitle className="text-lg flex items-center justify-between">
                            <span>{report.franchise_name}</span>
                            <span className="text-red-600 font-bold">₹{report.total_expenses?.toFixed(2) || '0.00'}</span>
                          </CardTitle>
                        </CardHeader>
                        <CardContent className="py-3">
                          <div className="grid grid-cols-2 gap-4 mb-3">
                            <div className="bg-green-50 p-3 rounded-lg">
                              <p className="text-sm text-green-700">Cash Expenses</p>
                              <p className="text-xl font-bold text-green-600">₹{report.cash_expenses?.toFixed(2) || '0.00'}</p>
                            </div>
                            <div className="bg-blue-50 p-3 rounded-lg">
                              <p className="text-sm text-blue-700">Online Expenses</p>
                              <p className="text-xl font-bold text-blue-600">₹{report.online_expenses?.toFixed(2) || '0.00'}</p>
                            </div>
                          </div>
                          {report.category_breakdown?.length > 0 && (
                            <div>
                              <p className="text-sm font-medium mb-2">Category Breakdown:</p>
                              <div className="flex flex-wrap gap-2">
                                {report.category_breakdown.map((cat, catIndex) => (
                                  <div key={catIndex} className="bg-gray-100 px-3 py-1 rounded-full text-sm">
                                    {cat.category}: <span className="font-bold">₹{cat.total?.toFixed(2)}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-secondary py-8">Click "Generate Report" to view franchise expense reports</p>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {activeSection === 'actuals' && (
          <div>
            <h2 className="text-3xl font-bold text-primary mb-6">Franchise Actuals</h2>
            <Card className="mb-6 shadow-sm border-border">
              <CardContent className="p-6">
                {/* Quick Date Filters */}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
                  <Button variant="outline" onClick={() => {
                    const today = new Date().toISOString().split('T')[0];
                    setActualsFilters({ ...actualsFilters, start_date: today, end_date: today });
                  }} className="h-10 rounded-full font-bold">Today</Button>
                  <Button variant="outline" onClick={() => {
                    const yesterday = new Date();
                    yesterday.setDate(yesterday.getDate() - 1);
                    const dateStr = yesterday.toISOString().split('T')[0];
                    setActualsFilters({ ...actualsFilters, start_date: dateStr, end_date: dateStr });
                  }} className="h-10 rounded-full font-bold">Yesterday</Button>
                  <Button variant="outline" onClick={() => {
                    const today = new Date();
                    const last7Days = new Date();
                    last7Days.setDate(today.getDate() - 7);
                    setActualsFilters({...actualsFilters, start_date: last7Days.toISOString().split('T')[0], end_date: today.toISOString().split('T')[0]});
                  }} className="h-10 rounded-full font-bold">Last 7 Days</Button>
                  <Button variant="outline" onClick={() => {
                    const today = new Date();
                    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
                    setActualsFilters({...actualsFilters, start_date: firstDay.toISOString().split('T')[0], end_date: today.toISOString().split('T')[0]});
                  }} className="h-10 rounded-full font-bold">This Month</Button>
                  <Button variant="outline" onClick={() => setActualsFilters({ franchise_id: 'all', start_date: '', end_date: '' })} className="h-10 rounded-full font-bold">Clear</Button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  <div>
                    <Label htmlFor="actuals-franchise">Franchise</Label>
                    <div className="relative">
                      <button
                        type="button"
                        onClick={() => setActualsFilters({ ...actualsFilters, franchise_id: 'all' })}
                        className={`absolute right-2 top-2 z-20 text-xs px-2 py-1 rounded-full font-medium transition-colors ${
                          actualsFilters.franchise_id === 'all' 
                            ? 'bg-accent text-white' 
                            : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                        }`}
                      >
                        All
                      </button>
                      <SearchableDropdown
                        options={franchises}
                        value={actualsFilters.franchise_id === 'all' ? '' : actualsFilters.franchise_id}
                        onChange={(value) => setActualsFilters({ ...actualsFilters, franchise_id: value || 'all' })}
                        placeholder="Search franchise by name or city..."
                        getOptionLabel={(f) => `${f.name} - ${f.location}`}
                        getOptionValue={(f) => f.id}
                        searchKeys={['name', 'location', 'id']}
                        className="relative"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="actuals-start-date">Start Date</Label>
                    <Input id="actuals-start-date" type="date" value={actualsFilters.start_date} onChange={(e) => setActualsFilters({ ...actualsFilters, start_date: e.target.value })} className="h-12 rounded-xl" />
                  </div>
                  <div>
                    <Label htmlFor="actuals-end-date">End Date</Label>
                    <Input id="actuals-end-date" type="date" value={actualsFilters.end_date} onChange={(e) => setActualsFilters({ ...actualsFilters, end_date: e.target.value })} className="h-12 rounded-xl" />
                  </div>
                </div>
                <Button onClick={handleGenerateActualsReport} className="w-full h-12 bg-primary text-white hover:bg-primary/90 rounded-full font-bold uppercase tracking-wider">
                  Generate Actuals Report
                </Button>
              </CardContent>
            </Card>

            {actualsData && (
              <Card className="shadow-sm border-border">
                <CardHeader>
                  <CardTitle className="text-xl font-bold text-primary">Actuals Data</CardTitle>
                </CardHeader>
                <CardContent>
                  {actualsData.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="w-full border-collapse">
                        <thead>
                          <tr className="bg-muted">
                            <th className="border border-border p-3 text-left font-bold">Date & Time</th>
                            <th className="border border-border p-3 text-left font-bold">Franchise Name</th>
                            <th className="border border-border p-3 text-right font-bold">Turnover (₹)</th>
                            <th className="border border-border p-3 text-right font-bold">Milk Consumed (L)</th>
                          </tr>
                        </thead>
                        <tbody>
                          {actualsData.map((actual, index) => (
                            <tr key={index} className="hover:bg-muted/50">
                              <td className="border border-border p-3">{new Date(actual.date_time).toLocaleString()}</td>
                              <td className="border border-border p-3">{actual.franchise_name}</td>
                              <td className="border border-border p-3 text-right font-bold">₹{parseFloat(actual.turnover).toFixed(2)}</td>
                              <td className="border border-border p-3 text-right">{parseFloat(actual.milk_consumed).toFixed(2)}</td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot>
                          <tr className="bg-accent text-white font-bold">
                            <td className="border border-border p-3" colSpan="2">Total</td>
                            <td className="border border-border p-3 text-right">
                              ₹{actualsData.reduce((sum, actual) => sum + parseFloat(actual.turnover), 0).toFixed(2)}
                            </td>
                            <td className="border border-border p-3 text-right">
                              {actualsData.reduce((sum, actual) => sum + parseFloat(actual.milk_consumed), 0).toFixed(2)} L
                            </td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  ) : (
                    <p className="text-center text-secondary py-8">No actuals data for selected period</p>
                  )}
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {activeSection === 'ledgers' && (
          <div>
            <h2 className="text-3xl font-bold text-primary mb-6">Party Ledgers - All Franchises</h2>
            <Card className="mb-6 shadow-sm border-border">
              <CardContent className="p-6">
                <Button
                  data-testid="load-ledgers-button"
                  onClick={fetchAllLedgers}
                  className="w-full h-12 bg-primary text-white hover:bg-primary/90 rounded-full font-bold uppercase tracking-wider"
                >
                  Load All Ledgers
                </Button>
              </CardContent>
            </Card>

            {allLedgers.length > 0 && (
              <Card className="shadow-sm border-border">
                <CardHeader>
                  <CardTitle className="text-xl font-bold text-primary">All Party Ledgers</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse">
                      <thead>
                        <tr className="bg-muted">
                          <th className="border border-border p-3 text-left font-bold">Party Name</th>
                          <th className="border border-border p-3 text-left font-bold">Phone Number</th>
                          <th className="border border-border p-3 text-left font-bold">Franchise</th>
                          <th className="border border-border p-3 text-right font-bold">Balance</th>
                          <th className="border border-border p-3 text-center font-bold">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {allLedgers.map((ledger, index) => (
                          <tr key={index} className="hover:bg-muted/50">
                            <td className="border border-border p-3 font-medium">{ledger.party_name}</td>
                            <td className="border border-border p-3">{ledger.phone_number}</td>
                            <td className="border border-border p-3">{ledger.franchise_name}</td>
                            <td className={`border border-border p-3 text-right font-bold ${
                              parseFloat(ledger.balance) >= 0 ? 'text-green-600' : 'text-red-600'
                            }`}>
                              ₹{parseFloat(ledger.balance).toFixed(2)}
                            </td>
                            <td className="border border-border p-3 text-center">
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-8 rounded-full"
                                onClick={() => viewPartyLedgerDetails(ledger)}
                              >
                                View Details
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {activeSection === 'setup' && (
          <div>
            <h2 className="text-3xl font-bold text-primary mb-6">System Setup</h2>
            <Card className="shadow-sm border-border">
              <CardHeader>
                <CardTitle className="text-xl font-bold text-primary">Initialize Google Sheets</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-secondary mb-4">This will create sample menu items and inventory in your Google Sheets database. Run this after setting up your service account credentials.</p>
                <Button data-testid="initialize-sheets-button" onClick={handleInitializeSheets} className="h-12 bg-primary text-white hover:bg-primary/90 rounded-full px-6 font-bold uppercase tracking-wider">
                  Initialize Sheets
                </Button>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Raw Materials Management Section */}
        {activeSection === 'raw-materials' && (
          <RawMaterialsManagement />
        )}

        {/* Raw Material Orders Section */}
        {activeSection === 'raw-material-orders' && (
          <RawMaterialOrders franchises={franchises} />
        )}
        
        {/* Cafe 1946 Expense Tracker - Full Screen */}
        {activeSection === 'cafe' && (
          <CafeExpenseTracker onBack={() => setActiveSection('franchises')} />
        )}
        </div>
      </div>

      {/* Franchise Details Dialog */}
      <Dialog open={isFranchiseDetailsOpen} onOpenChange={setIsFranchiseDetailsOpen}>
        <DialogContent className="bg-surface max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-primary">Franchise Details</DialogTitle>
          </DialogHeader>
          {selectedFranchise && (
            <div className="space-y-4 mt-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-secondary font-medium">Franchise Name</p>
                  <p className="text-base font-bold text-primary">{selectedFranchise.name}</p>
                </div>
                <div>
                  <p className="text-sm text-secondary font-medium">Franchise Code</p>
                  <p className="text-base font-bold text-primary">{selectedFranchise.id}</p>
                </div>
                <div>
                  <p className="text-sm text-secondary font-medium">Franchise Model</p>
                  <p className="text-base text-primary">{selectedFranchise.franchise_model}</p>
                </div>
                <div>
                  <p className="text-sm text-secondary font-medium">Franchise Amount</p>
                  <p className="text-base text-primary">₹{selectedFranchise.franchise_amount}</p>
                </div>
                <div>
                  <p className="text-sm text-secondary font-medium">Owner Name</p>
                  <p className="text-base text-primary">{selectedFranchise.contact_person}</p>
                </div>
                <div>
                  <p className="text-sm text-secondary font-medium">Contact Number</p>
                  <p className="text-base text-primary">{selectedFranchise.contact_number}</p>
                </div>
                <div>
                  <p className="text-sm text-secondary font-medium">Aadhaar Number</p>
                  <p className="text-base text-primary">{selectedFranchise.aadhaar_no || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-sm text-secondary font-medium">PAN Number</p>
                  <p className="text-base text-primary">{selectedFranchise.pan_no || 'N/A'}</p>
                </div>
                <div className="md:col-span-2">
                  <p className="text-sm text-secondary font-medium">Owner's Address</p>
                  <p className="text-base text-primary">{selectedFranchise.owner_address}</p>
                </div>
                <div className="md:col-span-2">
                  <p className="text-sm text-secondary font-medium">Franchise Address</p>
                  <p className="text-base text-primary">{selectedFranchise.franchise_address}</p>
                </div>
                <div>
                  <p className="text-sm text-secondary font-medium">City</p>
                  <p className="text-base text-primary">{selectedFranchise.city}</p>
                </div>
                <div>
                  <p className="text-sm text-secondary font-medium">Pincode</p>
                  <p className="text-base text-primary">{selectedFranchise.pincode}</p>
                </div>
                <div>
                  <p className="text-sm text-secondary font-medium">District</p>
                  <p className="text-base text-primary">{selectedFranchise.district}</p>
                </div>
                <div>
                  <p className="text-sm text-secondary font-medium">State</p>
                  <p className="text-base text-primary">{selectedFranchise.state}</p>
                </div>
                <div>
                  <p className="text-sm text-secondary font-medium">Date of Opening</p>
                  <p className="text-base text-primary">{selectedFranchise.date_of_opening}</p>
                </div>
                {selectedFranchise.additional_owners && (
                  <div className="md:col-span-2">
                    <p className="text-sm text-secondary font-medium">Additional Owners/Incharge</p>
                    <p className="text-base text-primary">{selectedFranchise.additional_owners}</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Party Ledger Details Dialog */}
      <Dialog open={isPartyLedgerDetailsOpen} onOpenChange={setIsPartyLedgerDetailsOpen}>
        <DialogContent className="bg-surface max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-primary">Party Ledger Details</DialogTitle>
          </DialogHeader>
          {selectedPartyLedger && (
            <div className="space-y-4 mt-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-secondary font-medium">Party Name</p>
                  <p className="text-base font-bold text-primary">{selectedPartyLedger.party_name}</p>
                </div>
                <div>
                  <p className="text-sm text-secondary font-medium">Phone Number</p>
                  <p className="text-base text-primary">{selectedPartyLedger.phone_number}</p>
                </div>
                <div>
                  <p className="text-sm text-secondary font-medium">Franchise</p>
                  <p className="text-base text-primary">{selectedPartyLedger.franchise_name}</p>
                </div>
                <div>
                  <p className="text-sm text-secondary font-medium">Current Balance</p>
                  <p className={`text-2xl font-bold ${
                    parseFloat(selectedPartyLedger.balance) >= 0 ? 'text-green-600' : 'text-red-600'
                  }`}>
                    ₹{parseFloat(selectedPartyLedger.balance).toFixed(2)}
                  </p>
                </div>
              </div>

              <div className="mt-6">
                <h3 className="text-lg font-bold text-primary mb-3">Transaction History</h3>
                {selectedPartyLedger.transactions && selectedPartyLedger.transactions.length > 0 ? (
                  <div className="space-y-2">
                    {selectedPartyLedger.transactions.map((txn, index) => {
                      // Check if transaction is an order (description format: "Order {order_no}")
                      const isOrder = txn.description && txn.description.startsWith('Order ');
                      const orderNo = isOrder ? txn.description.replace('Order ', '') : null;
                      
                      return (
                        <div
                          key={index}
                          className={`p-3 bg-muted rounded-lg flex justify-between items-center ${
                            isOrder ? 'cursor-pointer hover:bg-muted/70 transition-colors' : ''
                          }`}
                          onClick={() => {
                            if (isOrder && orderNo) {
                              navigate(`/order/${orderNo}`);
                            }
                          }}
                        >
                          <div>
                            <p className={`font-medium text-primary ${isOrder ? 'hover:underline' : ''}`}>
                              {txn.description || 'Transaction'}
                              {isOrder && <span className="ml-2 text-xs text-accent">(Click to view)</span>}
                            </p>
                            <p className="text-xs text-secondary">{new Date(txn.date_time).toLocaleString()}</p>
                          </div>
                          <div className="text-right">
                            <p className={`text-lg font-bold ${
                              txn.type.toUpperCase() === 'CREDIT' ? 'text-green-600' : 'text-red-600'
                            }`}>
                              {txn.type.toUpperCase() === 'CREDIT' ? '+' : '-'}₹{Math.abs(parseFloat(txn.amount)).toFixed(2)}
                            </p>
                            <p className="text-xs text-secondary">Balance: ₹{parseFloat(txn.balance_after).toFixed(2)}</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-center text-secondary py-4">No transactions yet</p>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Category Selection Modal */}
      <Dialog open={isCategoryModalOpen} onOpenChange={setIsCategoryModalOpen}>
        <DialogContent className="bg-surface max-w-md">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-primary">Select Category</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {/* Existing Categories */}
            <div>
              <Label className="text-sm font-medium text-secondary">Existing Categories</Label>
              <div className="mt-2 space-y-2 max-h-60 overflow-y-auto">
                {categories.length > 0 ? (
                  categories.map((cat) => (
                    <Button
                      key={cat}
                      type="button"
                      variant="outline"
                      className="w-full h-12 justify-start text-left rounded-xl hover:bg-accent hover:text-white transition-colors"
                      onClick={() => {
                        if (categoryModalFor === 'new') {
                          setNewMenuItem({ ...newMenuItem, category: cat });
                        } else {
                          setEditMenuItem({ ...editMenuItem, category: cat });
                        }
                        setIsCategoryModalOpen(false);
                        setNewCategoryName('');
                      }}
                    >
                      <span className="font-medium">{cat}</span>
                    </Button>
                  ))
                ) : (
                  <p className="text-sm text-secondary text-center py-4">No categories yet. Create one below!</p>
                )}
              </div>
            </div>

            {/* Divider */}
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-border"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-surface text-secondary">OR</span>
              </div>
            </div>

            {/* Create New Category */}
            <div>
              <Label className="text-sm font-medium text-secondary">Create New Category</Label>
              <div className="mt-2 flex gap-2">
                <Input
                  placeholder="Enter new category name"
                  value={newCategoryName}
                  onChange={(e) => setNewCategoryName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && newCategoryName.trim()) {
                      e.preventDefault();
                      const trimmedCategory = newCategoryName.trim();
                      if (!categories.includes(trimmedCategory)) {
                        setCategories([...categories, trimmedCategory]);
                        if (categoryModalFor === 'new') {
                          setNewMenuItem({ ...newMenuItem, category: trimmedCategory });
                        } else {
                          setEditMenuItem({ ...editMenuItem, category: trimmedCategory });
                        }
                        setIsCategoryModalOpen(false);
                        setNewCategoryName('');
                        toast.success(`Category "${trimmedCategory}" created!`);
                      } else {
                        toast.error('Category already exists');
                      }
                    }
                  }}
                  className="h-12 rounded-xl"
                />
                <Button
                  type="button"
                  className="h-12 bg-accent hover:bg-accent/90 text-white px-6 rounded-xl font-bold"
                  onClick={() => {
                    const trimmedCategory = newCategoryName.trim();
                    if (trimmedCategory) {
                      if (!categories.includes(trimmedCategory)) {
                        setCategories([...categories, trimmedCategory]);
                        if (categoryModalFor === 'new') {
                          setNewMenuItem({ ...newMenuItem, category: trimmedCategory });
                        } else {
                          setEditMenuItem({ ...editMenuItem, category: trimmedCategory });
                        }
                        setIsCategoryModalOpen(false);
                        setNewCategoryName('');
                        toast.success(`Category "${trimmedCategory}" created!`);
                      } else {
                        toast.error('Category already exists');
                      }
                    } else {
                      toast.error('Please enter a category name');
                    }
                  }}
                >
                  Create
                </Button>
              </div>
              <p className="text-xs text-secondary mt-2">Press Enter or click Create to add new category</p>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Device Info Modal */}
      <DeviceInfoModal
        isOpen={isDeviceInfoOpen}
        onClose={() => setIsDeviceInfoOpen(false)}
        franchise={selectedFranchise}
      />
    </div>
  );
};

export default AdminDashboard;